import { TicketKey } from 'flow/types/EntityKeys';
import { twMerge } from 'tailwind-merge';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectOriginDTOSubtype } from 'flow/selectors/TicketsSelectors';
import Icon from 'flow/components/ui-components/Icon';
import { getOriginIconKey } from 'flow/utils/ticket-channel-utils';

type ChannelIconSize = 'small' | 'medium';

interface Props {
    ticketKey: TicketKey;
    size?: ChannelIconSize;
    outlined?: boolean;
    // When provided, the icon is wrapped in a rounded backplate of this color.
    // Omit to render the icon without a backplate.
    colorBackplates?: string;
    // De-emphasise the icon (mid-gray) — used for closed tickets.
    muted?: boolean;
}

const WRAPPER_CLASSES: Record<ChannelIconSize, string> = {
    small: 'size-5',
    medium: 'size-6',
};

const INNER_ICON_SIZES: Record<ChannelIconSize, string> = {
    small: '1rem',
    medium: '1.25rem',
};

const ChannelIcon = ({
    ticketKey,
    size = 'small',
    outlined,
    colorBackplates,
    muted,
}: Props) => {
    const originSubtype = useAppSelector((state) =>
        selectOriginDTOSubtype(state, ticketKey),
    );

    const iconKey = getChannelIcon(originSubtype, outlined);

    if (!iconKey) return null;

    const iconElement = (
        <Icon
            iconKey={iconKey}
            className={muted ? 'text-gray-500' : 'text-gray-600'}
            size={INNER_ICON_SIZES[size]}
        />
    );

    if (!colorBackplates) return iconElement;

    return (
        <div
            className={twMerge(
                'flex items-center justify-center rounded-sm group-hover:bg-gray-300',
                WRAPPER_CLASSES[size],
                colorBackplates,
            )}
        >
            {iconElement}
        </div>
    );
};

export default ChannelIcon;

export const getChannelIcon = (channel?: string, outlined?: boolean) =>
    getOriginIconKey(channel, outlined);
